import React, { useState } from 'react'
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifFormField,
  MotifFileUploader,
  MotifFileUploaderItem,
  MotifIcon,
  MotifModalFooter,
  MotifButton,
} from '@ey-xd/motif-react'
import { IconoirUpload } from '@ey-xd/motif-icon'
import { formatBytes } from '@/utils/common'
import { fileExtensions } from '@/services/documents/documentsReducer'
import {
  useSupplymentEvidenceMutation
} from '@/services/application/evidenceReducer'
import { useDispatch } from 'react-redux'
import { setSupplementEvidence } from './DeveloperEvidenceSlice'


type Props = {
  isVisible: boolean
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>
  groupId: string
  supplementAccountName: string
  supplementAccountId: string
  canSkip?: boolean
  handleSkip: () => void
}

export default function UploadSupplymentEvidence({
  isVisible,
  setIsVisible,
  groupId,
  supplementAccountName,
  supplementAccountId,
  canSkip,
  handleSkip,
}: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [isError, setIsError] = useState<boolean | false>(false)
  const [isUploaded, setIsUploaded] = useState<boolean | false>(false)
  const dispatch = useDispatch()
  const [uploadDocument] = useSupplymentEvidenceMutation()

  const handleFileChange = async (files: any) => {
    if (files.length === 0) {
      return
    }
    if (files[0] && Object.keys(fileExtensions.default).includes(files[0].type)) {
      setFile(files[0])
    } else {
      setIsError(true)
    }
  }

  const handleFileUploadCancel = async () => {
    dispatch(
        setSupplementEvidence({
          isSupplymentRequired: false,
          evidenceGroupId: "",
          isSupplymentUploaded: false,
          supplementAccountName: undefined,
          supplementAccountId: undefined,
        }),
      )
      setIsVisible(false)
      setFile(null)
      setIsError(false)
      setIsUploaded(false)
  }

  const handleConfirmUpload = async () => {
    const formData = new FormData()
    if (file) {
      formData.append('file', file)
      formData.append('Name', file.name.replace(/\.[^/.]+$/, ''))
      formData.append('FileName', file.name)
      formData.append('supplementAccountId', supplementAccountId)
    }
    const response = await uploadDocument({body:formData, groupId}).unwrap()
    if (response) {
        dispatch(
            setSupplementEvidence({
              isSupplymentRequired: false,
              evidenceGroupId: "",
              isSupplymentUploaded: true,
              supplementAccountName: undefined,
              supplementAccountId: undefined,
            }),
          )
      setIsVisible(false)
      setFile(null)
      setIsUploaded(false)
    }
  }

  return (
    <>
      <MotifModal
        onClose={() => handleFileUploadCancel()}
        show={isVisible}
        size="lg"
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: 'none',
          },
        }}
      >
        <MotifModalHeader className="no-border-bottom">
          <div className="motif-h6-default-regular">Upload evidence for system account ({supplementAccountName})</div>
        </MotifModalHeader>
        <MotifModalBody>
          <MotifFormField>
            <MotifFileUploader
              id="supplyment-document-upload"
              label="Click to Upload or drag and drop "
              labelDescription={'Maximum file size : 20MB , File Format : JPG,JPEG,GIF,BMP,PNG or PDF'}
              onDrop={(files) => handleFileChange(files)}
              maxFileSize={10000}
              disabled={file !== null}
              accept={fileExtensions.default}
            >
              {file && (
                <MotifFileUploaderItem
                  fileIcon={<MotifIcon src={IconoirUpload} />}
                  fileName={file.name}
                  uploaded={true}
                  uploadedText={formatBytes(file.size)}
                  error={isError}
                  errorText={'Invalid file format'}
                  isIndeterminate
                  className="file-uploader-item"
                />
              )}
            </MotifFileUploader>
          </MotifFormField>
        </MotifModalBody>
        <MotifModalFooter>
          <div className="motif-row justify-content-end gap-1">
            {canSkip && (
            <MotifButton
              onClick={handleSkip}
              size="medium"
              type="button"
              variant="secondary"
            >
              Skip
            </MotifButton>
            )}
            <MotifButton
              onClick={() => handleFileUploadCancel()}
              size="medium"
              type="button"
              variant="secondary"
            >
              Cancel
            </MotifButton>
            <MotifButton
              className=""
              onClick={() => handleConfirmUpload()}
              size="medium"
              type="button"
              variant="primary"
            >
              Upload
            </MotifButton>
          </div>
        </MotifModalFooter>
      </MotifModal>
    </>
  )
}
